import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, invoicesService } from '../services/api';

const VAT_RATE_OPTIONS = [0, 5, 20];

export default function InvoiceSettingsPage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();
  const logoFileRef = useRef<HTMLInputElement>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['invoice-settings'],
    queryFn: () => invoicesService(createApiClient(getToken)).getSettings(),
    enabled: !!account,
  });

  const [serialPrefix, setSerialPrefix] = useState('INV');
  const [nextSerialNumber, setNextSerialNumber] = useState(1);
  const [defaultVatRate, setDefaultVatRate] = useState(20);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [defaultNotes, setDefaultNotes] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!settings) return;
    setSerialPrefix(settings.serialPrefix);
    setNextSerialNumber(settings.nextSerialNumber);
    setDefaultVatRate(settings.defaultVatRate);
    setShowBankDetails(settings.showBankDetails);
    setDefaultNotes(settings.defaultNotes ?? '');
    setLogoUrl(settings.logoUrl);
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: () => invoicesService(createApiClient(getToken)).updateSettings({
      serialPrefix, nextSerialNumber, defaultVatRate, showBankDetails, defaultNotes, logoUrl,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice-settings'] });
      alert('Invoice settings saved.');
    },
    onError: (e: any) => alert(e.message ?? 'Failed to save settings.'),
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => invoicesService(createApiClient(getToken)).uploadLogo(file),
    onSuccess: (url) => setLogoUrl(url),
    onError: (e: any) => alert(e.message ?? 'Failed to upload logo.'),
  });

  if (isLoading) return <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>;

  return (
    <div style={{ width: '100%', padding: '24px 16px' }}>
      <Link to="/invoices" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>← Invoices</Link>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '8px 0 20px' }}>Invoice Settings</h1>

      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Logo</label>
        {logoUrl && <img src={logoUrl} alt="Invoice logo" style={{ maxHeight: 60, marginBottom: 8, display: 'block' }} />}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => logoFileRef.current?.click()} style={{
            fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
            border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
          }}>{logoUrl ? 'Change Logo' : 'Upload Logo'}</button>
          {logoUrl && (
            <button onClick={() => setLogoUrl(undefined)} style={{
              fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
              border: '1px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer',
            }}>Remove</button>
          )}
        </div>
        <input
          ref={logoFileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogoMutation.mutate(f); }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Serial Prefix</label>
          <input
            value={serialPrefix}
            maxLength={8}
            onChange={(e) => setSerialPrefix(e.target.value.toUpperCase())}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Next Number</label>
          <input
            type="number"
            value={nextSerialNumber}
            onChange={(e) => setNextSerialNumber(Number(e.target.value))}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
          />
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#9ca3af', marginTop: -12, marginBottom: 20 }}>
        Next invoice number: {serialPrefix}-{String(nextSerialNumber).padStart(4, '0')}
      </p>

      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Default VAT Rate</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {VAT_RATE_OPTIONS.map(rate => (
            <button key={rate} onClick={() => setDefaultVatRate(rate)} style={{
              fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 20,
              border: '1px solid ' + (defaultVatRate === rate ? '#2563eb' : '#d1d5db'),
              background: defaultVatRate === rate ? '#eff6ff' : '#fff',
              color: defaultVatRate === rate ? '#1d4ed8' : '#374151', cursor: 'pointer',
            }}>{rate}%</button>
          ))}
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13 }}>
        <input type="checkbox" checked={showBankDetails} onChange={(e) => setShowBankDetails(e.target.checked)} />
        Show bank details on invoices
      </label>

      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Default Notes</label>
        <textarea
          value={defaultNotes}
          onChange={(e) => setDefaultNotes(e.target.value)}
          rows={4}
          placeholder="e.g. Payment terms, bank details…"
          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
        />
      </div>

      <button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        style={{
          fontSize: 13, fontWeight: 600, padding: '10px 20px', borderRadius: 8,
          background: saveMutation.isPending ? '#e5e7eb' : '#2563eb',
          color: saveMutation.isPending ? '#9ca3af' : '#fff', border: 'none',
          cursor: saveMutation.isPending ? 'default' : 'pointer',
        }}
      >
        {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
      </button>
    </div>
  );
}
